/**
 * Outcome modal shown after a Data Import upload finishes.
 *
 * Replaces the previous toast: the row counts and any failed rows stay on
 * screen until dismissed, so a partial import can actually be read and acted
 * on rather than vanishing after a few seconds.
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { CheckCircle2, AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react';

export interface ImportResultSummary {
  file_name: string;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  errors: string[];
}

interface ImportResultModalProps {
  open: boolean;
  /** Human name of the import type, e.g. "Goods Receipt Notes (GRNs)". */
  typeName: string;
  /** Company or group the rows landed against; blank for global imports. */
  targetLabel: string;
  result: ImportResultSummary | null;
  onClose: () => void;
  /** Re-open the upload dialog for the same type. Shown only on failures. */
  onReupload: () => void;
}

/**
 * Renders the created/updated/skipped/failed breakdown plus per-row errors.
 *
 * @param result - the step payload returned by the upload endpoint
 * @returns the outcome dialog, or nothing when there is no result yet
 */
export default function ImportResultModal({
  open, typeName, targetLabel, result, onClose, onReupload,
}: ImportResultModalProps) {
  const failed = result?.rows_failed || 0;
  const clean = failed === 0;

  const stats = result ? [
    { label: 'Created', value: result.rows_created || 0, className: 'text-green-600' },
    { label: 'Updated', value: result.rows_updated || 0, className: 'text-blue-600' },
    { label: 'Skipped', value: result.rows_skipped || 0, className: 'text-muted-foreground' },
    {
      label: 'Failed',
      value: failed,
      className: failed > 0 ? 'text-red-600' : 'text-muted-foreground',
    },
  ] : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        {result && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${clean ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {clean
                    ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                    : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                </div>
                {clean ? 'Import complete' : 'Imported with issues'}
              </DialogTitle>
              <DialogDescription>
                {typeName}{targetLabel ? ` — ${targetLabel}` : ''}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-1">
              {result.file_name && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileSpreadsheet className="h-4 w-4 shrink-0" />
                  <span className="truncate">{result.file_name}</span>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2">
                {stats.map(s => (
                  <div key={s.label} className="rounded-lg border p-3 text-center">
                    <p className={`text-2xl font-semibold ${s.className}`}>{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {failed > 0 && (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-red-600">
                    {failed} row{failed === 1 ? '' : 's'} could not be imported
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border bg-red-50/40 p-3 space-y-1">
                    {result.errors?.length
                      ? result.errors.map((e, i) => (
                        <p key={i} className="text-xs text-red-800 leading-relaxed">• {e}</p>
                      ))
                      : <p className="text-xs text-muted-foreground">No row detail was returned for these failures.</p>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Fix these rows in the spreadsheet and re-upload — rows that already
                    imported are updated, not duplicated.
                  </p>
                </div>
              )}

              {clean && (
                <p className="text-sm text-muted-foreground">
                  Every row in the file was imported successfully.
                </p>
              )}
            </div>

            <DialogFooter>
              {failed > 0 && (
                <Button variant="outline" onClick={onReupload}>
                  <Upload className="h-4 w-4 mr-2" />Re-upload
                </Button>
              )}
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
